'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Ambient purple glow that reacts to scrolling. It eases toward the current
 * scroll position, so it visibly floats/trails as the user scrolls instead of
 * sitting pinned. Fixed-positioned and behind content (z-0), so it works on any
 * page — whether the page scrolls on the window or inside an inner container
 * (caught via a capture-phase listener).
 *
 * Paint-cost note: this is a viewport-sized surface with a 120–160px blur —
 * one of the most expensive things a mobile GPU can rasterize. It therefore
 * (a) mounts only AFTER the first frame has committed, so it never competes
 * with the LCP paint, and (b) carries no will-change — the compositor
 * promotes it while the ease animation runs anyway, and a permanently
 * promoted blurred layer held memory + first-frame raster cost for a
 * decoration.
 */
export function ScrollGlow({ className }: { className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [painted, setPainted] = useState(false);

  // Two rAFs = "after the first frame is on screen".
  useEffect(() => {
    let id2 = 0;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setPainted(true));
    });
    return () => {
      cancelAnimationFrame(id1);
      cancelAnimationFrame(id2);
    };
  }, []);

  // Keyed on `painted`: the div doesn't exist before then, so attaching on
  // first mount would find ref.current null and never re-run.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let raf = 0;
    let current = 0;

    const readScroll = (target: EventTarget | null) =>
      target instanceof HTMLElement &&
      target !== document.documentElement &&
      target !== document.body
        ? target.scrollTop
        : window.scrollY || document.documentElement.scrollTop || 0;

    let scrollTop = readScroll(null);
    current = scrollTop;

    const tick = () => {
      current += (scrollTop - current) * 0.1;
      // Offset is 0 at rest; while scrolling, `current` lags `scrollTop`, so the
      // glow drifts opposite the scroll and eases back — a soft floating motion.
      const offset = current - scrollTop;
      el.style.transform = `translate(-50%, ${offset.toFixed(1)}px)`;
      if (Math.abs(offset) > 0.3) {
        raf = requestAnimationFrame(tick);
      } else {
        el.style.transform = 'translate(-50%, 0)';
        raf = 0;
      }
    };

    const onScroll = (event: Event) => {
      scrollTop = readScroll(event.target);
      if (!raf) raf = requestAnimationFrame(tick);
    };

    window.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    });
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [painted]);

  if (!painted) return null;

  return (
    <div
      ref={ref}
      aria-hidden="true"
      className={
        'pointer-events-none fixed top-[10vh] left-1/2 z-0 h-[440px] w-[860px] rounded-full blur-[120px] sm:h-[560px] sm:w-[1040px] lg:h-[660px] lg:w-[1240px] lg:blur-[160px] ' +
        (className ?? '')
      }
      style={{
        transform: 'translate(-50%, 0)',
        // Vivid plum-purple bloom (matches the reference), fading out at the rim.
        background:
          'radial-gradient(ellipse at center, rgba(160,58,200,0.55) 0%, rgba(126,45,168,0.32) 45%, rgba(80,20,110,0) 75%)',
      }}
    />
  );
}
